import Portal from "@client/controls/Portal/component";
import { type DraggableHOCProps } from "@client/hocs/draggable";
import { isInRect } from "@client/lib/coords";
import { type LeftTop } from "@client/types";
import * as React from "react";
import { getStickSide } from "./lib";

import "./style.less";

export type StickSide = "left" | "right";

export interface StickActionsProps {
  onStick?: (side: StickSide, width?: number) => void;
  onUnstick?: () => void;
}

export type StickableProps = DraggableHOCProps &
  StickActionsProps & {
    parentContainerSelector: string;
    isSticked?: boolean;
    stickSide?: StickSide | null;
  };

export interface StickableOptionalProps {
  onStickClick?: (e: MouseEvent | React.MouseEvent) => void;
}

interface State {
  isDragging: boolean;
}
const StickableHOC = <OriginalProps extends {}>(
  DraggableCmp: React.ComponentType<OriginalProps & DraggableHOCProps & StickableOptionalProps>
): React.ComponentClass<OriginalProps & StickableProps> =>
  class StickableHOC extends React.PureComponent<OriginalProps & StickableProps, State> {
    private _leftRef = React.createRef<HTMLDivElement>();
    private _rightRef = React.createRef<HTMLDivElement>();

    public state: State = {
      isDragging: false
    };

    public render() {
      const { onStick, onUnstick, parentContainerSelector, stickSide, ...cmpProps } = this.props;
      return (
        <>
          <DraggableCmp
            {...(cmpProps as OriginalProps & DraggableHOCProps)}
            onDragStart={this._onDragStart}
            onDragged={this._onDragged}
            onDragEnd={this._onDragEnd}
            onStickClick={onStick ? this._onStickClick : undefined}
          />

          {this.state.isDragging && !!onStick && (
            <Portal>
              {(!this.props.stickSide || this.props.stickSide === "left") && (
                <div ref={this._leftRef} className="wsc_stickable_placeholder left" />
              )}
              {(!this.props.stickSide || this.props.stickSide === "right") && (
                <div ref={this._rightRef} className="wsc_stickable_placeholder right" />
              )}
            </Portal>
          )}
        </>
      );
    }

    _onDragStart = (el: Element) => {
      this.setState({ isDragging: true });
    };

    _onDragged = (lt: LeftTop, e: MouseEvent) => {
      this.props.onDragged(lt, e);

      if (this.props.isSticked) {
        this.props.onUnstick && this.props.onUnstick();
      }
    };

    _onDragEnd = (el: Element, lt: LeftTop, e: MouseEvent) => {
      this.props.onDragged(lt, e);

      const side = this._getStickSide(e);
      if (side) {
        this.props.onStick && this.props.onStick(side);
      }

      this.setState({ isDragging: false });
    };

    _onStickClick = (e: MouseEvent | React.MouseEvent) => {
      if (this.props.isSticked) {
        this.props.onUnstick && this.props.onUnstick();
      } else {
        if (this.props.stickSide) {
          this.props.onStick && this.props.onStick(this.props.stickSide);
        } else {
          if ("target" in e) {
            const parent = $(e.target as HTMLElement).closest(this.props.parentContainerSelector);
            if (parent.length) {
              const rect = parent.get(0)!.getBoundingClientRect();
              this.props.onStick &&
                this.props.onStick(getStickSide({ left: rect.left + rect.width / 2, top: rect.top }));
              return;
            }
          }
          this.props.onStick && this.props.onStick(getStickSide({ left: e.clientX, top: e.clientY }));
        }
      }
    };

    _getStickSide = (e: MouseEvent): StickSide | null | undefined => {
      if ((!this.props.stickSide || this.props.stickSide === "left") && this._isInside(this._leftRef, e)) return "left";
      if ((!this.props.stickSide || this.props.stickSide === "right") && this._isInside(this._rightRef, e))
        return "right";
    };

    _isInside = (ref: React.RefObject<HTMLDivElement | null>, e: MouseEvent): boolean => {
      if (!ref.current) return false;
      const rect = (ref.current as HTMLDivElement).getBoundingClientRect();
      return isInRect(rect, { left: e.clientX, top: e.clientY });
    };
  };
export default StickableHOC;
